/**
 * The Leasing routes. Shared by the routes' `beforeLoad` and the pages' `<ModuleGate>`, which look the
 * owning module up in the registry (`moduleAreaForPath`) rather than naming it — so Leasing owns these
 * screens through `packages/hetivo-lib-types/src/modules.ts` alone.
 */
export const LEASING_PATH = '/leasing';
export const LEASING_CONTRACTS_PATH = '/leasing/contracts';
/**
 * Where `/leasing` sends a user who cannot open Contracts (no `contracts:list`): the first other Leasing
 * page, which only needs `leasing:access` (it shows Coming soon). This also breaks the redirect loop — the
 * contract routes bounce a user without `contracts:list` to `/leasing`, so `/leasing` must not bounce back
 * to a contracts route for that same user (SWOOP-345).
 */
export const LEASING_ORDER_PIPELINE_PATH = '/leasing/order-pipeline';
/** SWOOP-322: the New contract page. Not a nav surface, so it is not in the module registry. */
export const LEASING_NEW_CONTRACT_PATH = '/leasing/contracts/new';

/**
 * SWOOP-346: the wizard's edit page route for a saved draft or an unsent Ready-to-send contract. Kept as a
 * single literal so the first-save redirect and the `?step` sync both navigate through one constant (its
 * literal type is what lets `navigate({ to, params })` type-check).
 */
export const LEASING_EDIT_CONTRACT_ROUTE = '/leasing/contracts/$contractId/edit';

/**
 * Where the wizard lands after Create contract, and where it sends a contract it can no longer edit
 * (SWOOP-346): that contract's own page, now that SWOOP-365 ships it.
 */
export function leasingContractLandingPath(id: string): string {
  return leasingContractPath(id);
}

/** The one permission behind every Leasing screen (SWOOP-295). It has no effect while the module is off. */
export const LEASING_ACCESS_PERMISSION = 'leasing:access';

/** Who may open a territory's lease options (SWOOP-339). Without it the Leasing tab shows a no-access notice and requests nothing. */
export const LEASING_SETTINGS_LIST_PERMISSION = 'leasing-settings:list';

/** Who may change a territory's lease options (SWOOP-339). Without it the tab is read-only: disabled fields, no save bar. */
export const LEASING_SETTINGS_MANAGE_PERMISSION = 'leasing-settings:manage';

/** Who may start a contract (SWOOP-322): the New contract button and route. It has no effect while the module is off. */
export const CONTRACTS_MANAGE_PERMISSION = 'contracts:manage';

/**
 * Who may duplicate a master contract as a new draft (SWOOP-397): the Duplicate action and the `?duplicateFrom`
 * wizard route. Deliberately separate from `contracts:manage` (which the Territory Admin also holds) — Duplicate
 * is the Account Manager and the Tenant Admin only. It has no effect while the module is off.
 */
export const CONTRACT_DUPLICATES_MANAGE_PERMISSION = 'contract-duplicates:manage';

/**
 * Whether the current user may duplicate a master contract (SWOOP-397): the dedicated
 * `contract-duplicates:manage` grant AND the baseline `contracts:manage` every contract write needs. Defined
 * once so the Duplicate action, its `?duplicateFrom` route and the New contract page all require the same
 * pair — a user who somehow holds only the duplicate grant still gets no action and is redirected.
 */
export function canDuplicateContracts(hasPermission: (permission: string) => boolean): boolean {
  return hasPermission(CONTRACT_DUPLICATES_MANAGE_PERMISSION) && hasPermission(CONTRACTS_MANAGE_PERMISSION);
}

/**
 * Who may generate a fee re-bill, today the stamp duty invoice request, on a signed contract (SWOOP-416): the
 * Generate stamp duty invoice action. Deliberately separate from `contracts:manage` (which the Territory Admin
 * also holds), as for duplication (SWOOP-397): the Account Manager and the Tenant Admin only. The server states
 * the verdict in `ContractMasterContract.feeRebill.canGenerate`, which the card reads; this constant is the
 * grant's name for any caller that needs it. It has no effect while the module is off.
 */
export const CONTRACT_FEE_REBILLS_MANAGE_PERMISSION = 'contract-fee-rebills:manage';

/** Who may read contracts (SWOOP-345): the Contracts list, its menu link and the contract address. It has no effect while the module is off. */
export const CONTRACTS_LIST_PERMISSION = 'contracts:list';

/**
 * Who may download / print the Lease Delivery Note and upload or replace its signed copy (SWOOP-366/398):
 * the Fleet Manager, Account Manager, Tenant Admin and Territory Admin.
 */
export const AGREEMENTS_LIST_PERMISSION = 'agreements:list';

/**
 * Who may assign / unassign a core vehicle to an agreement and record the handover (SWOOP-366/398): the
 * Fleet Manager only. A holder also sees the driver, date and odometer fields as editable.
 */
export const AGREEMENT_ASSIGNMENTS_MANAGE_PERMISSION = 'agreement-assignments:manage';

/** Who may manage relief vehicles on an agreement (SWOOP-399): the agreement ⋯ menu and every relief action. Reading reliefs follows `contracts:list`. */
export const AGREEMENTS_MANAGE_PERMISSION = 'agreements:manage';

/**
 * Who may modify an On lease agreement's monthly price, extra-km price or end date (SWOOP-414): the agreement
 * page's Modify button, the ⋯ "Modify agreement" item and the edit-mode save. Held by the Account Manager and
 * the Tenant Admin only — deliberately narrower than `agreements:list` (also held by the Fleet Manager and the
 * Territory Admin). A price or term change above 20 % additionally needs `agreement-terms-over-limit:manage`
 * (the Tenant Admin only), enforced server-side; the front office surfaces its refusal as a written message.
 */
export const AGREEMENT_TERMS_MANAGE_PERMISSION = 'agreement-terms:manage';

/** The address of one contract (SWOOP-345): the contract page (SWOOP-365). */
export function leasingContractPath(contractId: string): string {
  return `${LEASING_CONTRACTS_PATH}/${contractId}`;
}

/** The contract page route, as a literal for `navigate({ to, params, search })` (the agreement page's Back). */
export const LEASING_CONTRACT_ROUTE = '/leasing/contracts/$contractId';

/**
 * Who may open the agreement page (SWOOP-413 review): the agreement read (`agreements:list`) AND the contract read
 * (`contracts:list`), since the page shows the contract's customer, deposit and packages. Every built-in role with
 * one holds the other; this keeps a custom role holding only `agreements:list` from reaching a page it cannot load.
 */
export const AGREEMENT_PAGE_PERMISSIONS = [AGREEMENTS_LIST_PERMISSION, CONTRACTS_LIST_PERMISSION] as const;

/** The agreement page: one agreement of a contract, read only. A single literal so `<Link to params>` type-checks. */
export const LEASING_AGREEMENT_ROUTE = '/leasing/contracts/$contractId/agreements/$agreementId';

/** The Leasing tab of Territory Settings (SWOOP-302): per-territory leasing configuration. */
export const LEASING_TERRITORY_SETTINGS_PATH = '/settings/territories/$territoryId/leasing';
